/**
 * The frame around every sign-in page.
 *
 * Praxadon is where a child writes their first HTML, so its front door looks like the
 * thing they are about to open: a wall of code, and an editor window with the form in
 * it. The class's welcome is the comment at the top of the file, the fields sit inside
 * a `<form>`, and the status bar says `ready`.
 *
 * None of that is allowed to cost anything. Every flourish is `aria-hidden`, out of the
 * tab order and unselectable; the form inside is the product's ordinary labelled form
 * on a solid surface, because a password field that is hard to read is the one thing
 * this page cannot afford. A class that would rather have a photograph still can
 * (Class settings → Your sign-in page), and the words above the window follow the
 * picture's tone so they stay readable on it.
 *
 * Everything is served from this origin. The content policy allows images from nowhere
 * else, and a sign-in page is exactly where that rule earns its keep: anyone who can
 * put an image on this page can watch every visit to it.
 */

import { type ReactNode } from 'react';

import { cn } from '@praxadon/ui';

import { BrandMark } from '../../app/Brand.tsx';
import { siteName, useSession } from '../../app/session.tsx';
import {
  DEFAULT_BACKGROUND,
  backgroundSrc,
  findBackground,
  type Background,
} from './backgrounds.ts';
import { CodeBackdrop } from './CodeBackdrop.tsx';
import { CodeLine, EditorFrame } from './EditorFrame.tsx';

export type Overlay = 'soft' | 'medium' | 'strong';

export type Chosen =
  | { kind: 'code'; tone: 'dark' }
  | { kind: 'image'; src: string; srcSet?: string; colour: string; tone: Background['tone'] };

/** A class's own uploaded picture, one of ours, or the wall of code. */
export function chooseBackground(chosen: string | undefined): Chosen {
  // An uploaded picture is served from this origin, under /api/public/branding. Its
  // tone is unknown, so it is treated as dark: dimmed, with white text over it.
  if (chosen?.startsWith('/api/public/branding/')) {
    return { kind: 'image', src: chosen, colour: '#0b0d12', tone: 'dark' };
  }
  const picture = findBackground(chosen) ?? findBackground(DEFAULT_BACKGROUND);
  if (!picture || picture.kind === 'code') return { kind: 'code', tone: 'dark' };
  return {
    kind: 'image',
    src: backgroundSrc(picture.id, 1920),
    srcSet: `${backgroundSrc(picture.id, 1080)} 1080w, ${backgroundSrc(picture.id, 1920)} 1920w`,
    colour: picture.colour,
    tone: picture.tone,
  };
}

const SCRIM: Record<Background['tone'], Record<Overlay, string>> = {
  dark: { soft: 'bg-black/30', medium: 'bg-black/50', strong: 'bg-black/70' },
  light: { soft: 'bg-white/40', medium: 'bg-white/60', strong: 'bg-white/80' },
};

/** The backdrop — code or a picture — with whatever sits on top of it. */
export function Backdrop({
  picture,
  overlay = 'medium',
  children,
}: {
  picture: string | undefined;
  overlay?: Overlay | undefined;
  children: (tone: Background['tone']) => ReactNode;
}) {
  const chosen = chooseBackground(picture);

  if (chosen.kind === 'code') {
    return (
      <div className="relative isolate min-h-dvh bg-[#080a12]">
        <CodeBackdrop />
        {children('dark')}
      </div>
    );
  }

  return (
    <div className="relative isolate min-h-dvh" style={{ backgroundColor: chosen.colour }}>
      <img
        src={chosen.src}
        {...(chosen.srcSet ? { srcSet: chosen.srcSet, sizes: '100vw' } : {})}
        alt=""
        aria-hidden
        // Aimed a little above centre: the interesting part of most pictures is the
        // upper half, and that is where the heading sits.
        className="absolute inset-0 -z-10 size-full object-cover object-[50%_40%]"
      />
      <div aria-hidden className={cn('absolute inset-0 -z-10', SCRIM[chosen.tone][overlay])} />
      {/* A pool of shade for the window to sit in, so it is not stuck on the photograph. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            chosen.tone === 'light'
              ? 'radial-gradient(65% 55% at 50% 45%, rgba(255,255,255,.55), transparent 70%)'
              : 'radial-gradient(65% 55% at 50% 45%, rgba(0,0,0,.45), transparent 70%)',
        }}
      />
      {children(chosen.tone)}
    </div>
  );
}

/** Text that sits on the backdrop rather than inside the window. */
export function onPicture(tone: Background['tone'], strength: 'strong' | 'soft'): string {
  if (tone === 'light') {
    return strength === 'strong' ? 'text-slate-900' : 'text-slate-700';
  }
  return strength === 'strong' ? 'text-white drop-shadow-lg' : 'text-white/75 drop-shadow';
}

/**
 * The white-labelled frame: the class's address, then its file.
 *
 * A logo is shown only from this site's own origin, for the same reason as the picture.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  file = 'sign-in.html',
  form = true,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** The name on the window's tab. Each page names its own. */
  file?: string;
  /**
   * Whether the children are a form. A page that only says something — an expired
   * invitation, a password already changed — is not wrapped in a form tag.
   */
  form?: boolean;
}) {
  const { site } = useSession();
  // `sign-up.html` posts to `/sign-up`: the tab and the form line always agree.
  const action = `/${file.replace(/\.\w+$/, '')}`;
  const branding = site.kind === 'tenant' ? site.tenant.branding : {};
  const logo = branding.logoUrl?.startsWith('/') ? branding.logoUrl : undefined;

  return (
    <Backdrop picture={branding.loginBackground} overlay={branding.loginOverlay}>
      {(tone) => (
        <div className="flex min-h-dvh flex-col">
          <main className="flex flex-1 items-center justify-center px-4 py-10">
            <div className="w-full max-w-[30rem]">
              {/* Above the window: whose class this is, written like a path. */}
              <div className="rise-in mb-4 flex items-center justify-center gap-2.5">
                {logo ? (
                  <img src={logo} alt="" className="h-8 w-auto" />
                ) : (
                  <BrandMark size={30} className="drop-shadow" />
                )}
                <span className={cn('font-mono text-sm', onPicture(tone, 'soft'))}>
                  <span aria-hidden className="opacity-60">
                    ~/
                  </span>
                  {siteName(site)}
                </span>
              </div>

              <EditorFrame file={file} className="rise-in">
                <div className="space-y-4">
                  <div>
                    {/* The class's own welcome, as the comment at the top of the file. */}
                    <h1 className="no-ligatures font-mono text-base leading-relaxed text-fg-subtle">
                      <span aria-hidden className="whitespace-nowrap opacity-70">
                        {'<!-- '}
                      </span>
                      <span className="text-fg">{title}</span>
                      <span aria-hidden className="whitespace-nowrap opacity-70">
                        {' -->'}
                      </span>
                    </h1>
                    {subtitle ? (
                      <div className="no-ligatures mt-1 font-mono text-sm text-fg-subtle">
                        <span aria-hidden className="whitespace-nowrap opacity-70">
                          {'<!-- '}
                        </span>
                        {subtitle}
                        <span aria-hidden className="whitespace-nowrap opacity-70">
                          {' -->'}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {form ? (
                    <>
                      <CodeLine open={`<form action="${action}" method="post">`} />
                      <div className="pl-0 sm:pl-3">{children}</div>
                      <CodeLine open="</form>" />
                    </>
                  ) : (
                    children
                  )}
                </div>
              </EditorFrame>

              {footer ? (
                <div
                  className={cn(
                    'rise-in mt-4 text-center font-mono text-sm',
                    onPicture(tone, 'soft'),
                  )}
                >
                  {footer}
                </div>
              ) : null}
            </div>
          </main>

          <footer
            className={cn('px-4 pb-6 text-center font-mono text-xs', onPicture(tone, 'soft'))}
          >
            {branding.loginFooter ?? 'Powered by Praxadon IDE'}
            {branding.supportEmail ? (
              <>
                {' · '}
                <a className="underline" href={`mailto:${branding.supportEmail}`}>
                  Need help?
                </a>
              </>
            ) : null}
          </footer>
        </div>
      )}
    </Backdrop>
  );
}

/** A form-level error, announced to screen readers when it appears. */
export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="rounded-lg border border-danger/40 bg-danger-soft px-3 py-2 text-sm text-fg"
    >
      {message}
    </div>
  );
}
